<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >
    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('REQUEST_ASSIGNED_CARDS')"
            :label="$t('Request Batch Cards')"
            size="md"
            @click="gotoRequestCards"
          />
          <b-dropdown
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            class="ml-1"
            :text="$t('Export')"
            variant="primary"
          >
            <b-dropdown-item @click="csvExport">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport">
              {{ $t('Excel') }}
            </b-dropdown-item>
          </b-dropdown>
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="cardProfiles"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="batchCardRequestList"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(dateCreated)="data">
        {{ data.value ? toDateWithYear(data.value) : '' }}
      </template>
      <template #cell(lastDeliveryDate)="data">
        {{ data.value ? toDateWithYear(data.value) : '' }}
      </template>

      <template #cell(generationStatus)="data">
        <b-badge
          v-if="data.value"
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>
      <template #cell(fileGenerationStatus)="data">
        <b-badge
          v-if="data.value"
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <template #cell(canTakeDelivery)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value ? 'Yes' : 'No')"
        >
          {{ data.value ? $t('Yes') : $t('No') }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('RECEIVE_CARD_DELIVERY') && data.item.canTakeDelivery"
              @click="onTakeDelivery(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50"> {{ $t('Take Delivery') }} </span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="getUserPermission('REVOKE_CARD_REQUEST') && (data.item.generationStatus.toLowerCase() !== 'generated' || data.item.generationStatus.toLowerCase() !== 'delivered')"
              @click="onRevokeCard(data.item)"
            >
              <feather-icon icon="RotateCcwIcon" />
              <span class="align-middle ml-50"> {{ $t('Revoke Request') }} </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="data.item.cardFileLink"
              @click="downloadBatchCards(data.item, $event.target)"
            >
              <feather-icon icon="DownloadIcon" />
              <span class="align-middle ml-50"> {{ $t('Download Cards') }} </span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="data.item.encrptedZipFilePassword"
              @click="viewDownloadZipPassword(data.item.encrptedZipFilePassword)"
            >
              <feather-icon icon="LockIcon" />
              <span class="align-middle ml-50"> {{ $t('View Zip Password') }} </span>
            </b-dropdown-item>

          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="take-delivery-modal"
      ref="deliveryModal"
      :ok-title="$t('Take Delivery')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      no-close-on-backdrop
      size="lg"
      :title="$t('Take Batch Card Delivery')"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="takeDeliveryRules">
        <b-form>
          <b-row class="match-height">

            <!--   Batch Request ID -->
            <b-col
              md="6"
            >
              <InputField
                id="batch-request-ID"
                v-model="takeDeliveryCardsFormData.batchNo"
                :label="$t('Batch Request ID')"
                type="text"
                readonly
              />
            </b-col>
            <!--  Batch Request ID -->

            <!-- Card Request Type -->
            <b-col
              md="6"
            >
              <InputField
                id="card-request-type"
                v-model="takeDeliveryCardsFormData.cardRequestType"
                :label="$t('Card Request Type')"
                type="text"
                readonly
              />
            </b-col>
            <!-- Card Request Type -->

            <!-- Total No of Cards Requested -->
            <b-col
              md="6"
            >
              <InputField
                id="card-request-type"
                v-model="takeDeliveryCardsFormData.totalNoOfCardsRequested"
                :label="$t('Total No of Cards Requested')"
                type="text"
                vid="cardRequested"
                readonly
              />
            </b-col>
            <!-- Total No of Cards Requested -->

            <!-- Total No of Cards Delivered To Date -->
            <b-col
              md="6"
            >
              <InputField
                id="total-no-of-cards-delivered-to-date"
                v-model="takeDeliveryCardsFormData.totalNoOfCardsDelivered"
                :label="$t('Total No of Cards Delivered To Date')"
                type="text"
                vid="cardDeliveredToDate"
                readonly
              />
            </b-col>
            <!-- Total No of Cards Delivered To Date -->

            <!--  How Many Cards Did You Receive Now? -->
            <b-col md="6">
              <InputNumberField
                id="rate"
                v-model="takeDeliveryCardsFormData.cardsReceived"
                :label="$t('How Many Cards Did You Receive Now?')"
                type="text"
                :error-name="$t('Cards Received')"
                rules="required|cardNumber:@cardNumber|cardDeliveredAndReceived:@cardDeliveredToDate,@cardRequested|positive"
                @input="computeNumberOfCardsAwaitingDelivery"
              />
            </b-col>
            <!--  How Many Cards Did You Receive Now? -->

            <!--  Number of Cards Awaiting Delivery -->
            <b-col md="6">
              <InputNumberField
                id="rate"
                v-model="takeDeliveryCardsFormData.numberOfCardsAwaiting"
                :label="$t('Number of Cards Awaiting Delivery')"
                vid="cardNumber"
                readonly
              />
            </b-col>
            <!--  Number of Cards Awaiting Delivery -->
          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <b-modal
      id="modal-view-zip-password"
      ref="viewZipPassword"
      :ok-title="$t('Cancel')"
      ok-only
      :cancel-title="$t('Cancel')"
      ok-variant="outline-secondary"
      centered
      size="md"
      :title="$t('View Download Zip Password')"
    >
      <b-form>
        <b-row class="match-height">
          <b-col
            md="12"
            class="mt-2"
          >
            <InputPasswordField
              id="zip-password"
              v-model="encrptedZipFilePassword"
              :label="$t('Download ZIP Password')"
              readonly
            />
          </b-col>
          <b-col
            md="12"
            class="mb-2 mt-2"
          >
            <button
              class="btn btn-link p-0"
              variant="primary"
              @click.prevent="copy2ClipBoard"
            >
              <span style="font-size: 13px; font-weight: bold; cursor: pointer">
                {{ labelTitle }}
              </span>
            </button>
          </b-col>
        </b-row>
      </b-form>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchBatchCardsRequest"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!--  Batch Request ID -->
        <b-col md="6">
          <InputField
            id="batch-request-iD"
            v-model="filter.batchNo"
            :label="$t('Batch Request ID')"
            :placeholder="$t('Enter Batch Request ID')"
            type="text"
          />
        </b-col>
        <!--  Batch Request ID -->

        <!-- Card Profile Name-->
        <b-col md="6">
          <InputField
            id="card-profile-name"
            v-model="filter.cardProfileName"
            :label="$t('Card Profile Name')"
            :placeholder="$t('Enter Card Profile Name')"
            type="text"
          />
        </b-col>
        <!-- Card Profile Name-->

        <!--Card Request Type-->
        <b-col md="6">
          <SelectField
            id="card-request-type"
            v-model="filter.requestType"
            :label="$t('Card Request Type')"
            :placeholder="$t('Select')"
            :options="translatedCardRequestOptions"
            item="label"
            :reduce="option => option.value"
          />
        </b-col>
        <!--Card Request Type-->
        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.cardGenerationStatus"
            :label="$t('Card Generation Status')"
            :placeholder="$t('Select')"
            :options="translatedBatchCardStatusOptions"
            item="label"
            :reduce="option => option.value"
          />
        </b-col>
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.cardFilesGenerationStatus"
            :label="$t('Card File Generation Status')"
            :placeholder="$t('Select')"
            :options="translatedBatchCardStatusOptions"
            item="label"
            :reduce="option => option.value"
          />
        </b-col>
        <!-- Status -->

        <!--  Date of Request -->
        <b-col md="6">
          <DatePickerField
            id="date-of-request-from"
            v-model="filter.startDate"
            rules="required"
            :label="$t('Date of Request From')"
            :error-name="$t('Date of Request')"
            :config="datePickerConfig"
            :placeholder="$t('Date of Request')"
            :disabled="isReadonly"
          />
        </b-col>
        <b-col md="6">
          <DatePickerField
            id="date-of-request-to"
            v-model="filter.endDate"
            rules="required"
            :label="$t('Date of Request To')"
            :error-name="$t('Date of Request')"
            :config="datePickerConfig"
            :placeholder="$t('Date of Request')"
            :disabled="isReadonly"
          />
        </b-col>
        <!--  Date of Request -->

      </b-row>
    </b-modal>
    <!-- Filter modal -->
    <FullScreenSpinner :show="showSpinner" />
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BBadge, BModal, BDropdown, BDropdownItem, BForm,
} from 'bootstrap-vue'
import { mapActions } from 'vuex'
import { required, amount, positive } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { toastAlert } from '@core/mixins/toast'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import { permissionMixin } from '@core/mixins/permission'
import { ValidationObserver } from 'vee-validate'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import InputField from '../../../../forms/InputField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import InputPasswordField from '../../../../forms/InputPasswordField.vue'
import {
  cardGenerationStatusOptions, requestOptions,
} from '@/helpers/data'
import getStatusColor from '@/helpers/getStatusColor'
import Button from '../../../../components/button/Button.vue'
// import { checkTokenValidity } from '@/helpers/token'
import { toDateWithYear, toDateWithYearFirst } from '@/helpers/formatDate'
import { eChannelsPermission } from '@/helpers/permissions'
import DatePickerField from '../../../../forms/DatePickerField.vue'
import InputNumberField from '../../../../forms/InputNumberField.vue'
import handleZero from '@/helpers/handleZero'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BDropdown,
    BDropdownItem,
    BBadge,
    TablePagination,
    BModal,
    BSpinner,
    TableEntries,
    SelectField,
    InputField,
    InputPasswordField,
    Button,
    FullScreenSpinner,
    DatePickerField,
    InputNumberField,
    BForm,
    ValidationObserver,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, toastAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      isReadonly: false,
      isEditable: false,
      isFullName: false,
      showSpinner: false,
      sortBy: 'name',
      required,
      amount,
      positive,
      // batchCardStatusOptions,
      // cardRequestOptions,
      modalTitle: this.$t('Card Profile Setup'),
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'batchNo', sortable: true, label: 'Batch Request ID' },
        { key: 'cardProfileName', sortable: true, label: 'Card Profile Name' },
        { key: 'hostFIName', sortable: true, label: 'Host Financial Institution' },
        { key: 'requestType', sortable: true, label: 'Card Request Type' },
        { key: 'cardType', sortable: true, label: 'Card Type' },
        { key: 'dateCreated', sortable: true, label: 'Date Of Request' },
        { key: 'numberOfCardsRequested', sortable: true, label: 'No Of Cards Requested' },
        { key: 'numberOfCardsReceived', sortable: true, label: 'No Of Cards Delivered' },
        { key: 'numberOfCardsAwaitingDelivery', sortable: true, label: 'No Of Cards Awaiting Delivery' },
        { key: 'numberOfCardsIssued', sortable: true, label: 'No Of Cards Issued To Date' },
        { key: 'numberOfCardsUnIssued', sortable: true, label: 'No Of Cards Yet Unissued' },
        { key: 'generationStatus', sortable: true, label: 'Card Generation Status' },
        { key: 'fileGenerationStatus', sortable: true, label: 'Card File Generation Status' },
        { key: 'canTakeDelivery', sortable: true, label: 'Can Take Delivery' },
        { key: 'lastDeliveryDate', sortable: true, label: 'Date Of Last Delivery' },
        { key: 'requestingUserEmail', sortable: true, label: 'Requester Name' },
        { key: 'actions', label: 'Actions' },
      ],
      getStatusColor,
      buttonLabel: this.$t('Save'),
      filter: {
        startDate: null,
        endDate: null,
        batchNo: '',
        cardProfileName: '',
        requestType: '',
        cardGenerationStatus: '',
        cardFilesGenerationStatus: '',
        sortColumn: '',
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        altFormat: 'Y-m-d',
        ariaDateFormat: 'Y-m-d',
        defaultDate: '',
      },
      takeDeliveryCardsFormData: {
        batchNo: '',
        cardRequestType: '',
        totalNoOfCardsRequested: '',
        totalNoOfCardsDelivered: '',
        cardsReceived: '',
        numberOfCardsAwaiting: '',
      },
      batchCardRequestList: [],
      numberOfCards: null,
      encrptedZipFilePassword: null,
      labelTitle: this.$t('COPY ZIP PASSWORD'),
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedBatchCardStatusOptions() {
      return cardGenerationStatusOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedCardRequestOptions() {
      return requestOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchBatchCardsRequest()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchBatchCardsRequest()
    },
  },
  mounted() {
    this.fetchBatchCardsRequest()
  },
  methods: {
    ...mapActions({
      getBatchRequest: 'eChannels/getBatchRequestList',
      takeCardDelivery: 'eChannels/takeCardDeliveryAction',
      revokeCard: 'eChannels/revokeCardAction',
      downloadBatchCard: 'eChannels/downloadBatchCard',
      downloadCardGenerationReport: 'reports/downloadCardGenerationReport',
    }),
    viewDownloadZipPassword(encryptedPassword) {
      this.encrptedZipFilePassword = encryptedPassword
      this.$root.$emit('bv::show::modal', 'modal-view-zip-password')
    },
    copy2ClipBoard() {
      navigator.clipboard.writeText(this.encrptedZipFilePassword)
        .then(() => {
          this.labelTitle = this.$t('ZIP PASSWORD COPIED')
          if (this.labelTitle === this.$t('ZIP PASSWORD COPIED')) {
            this.toastAlert({ title: this.$t('Your ZIP Password has been copied') })
          }
        }).catch(
          e => {
            console.log(e)
          },
        )
    },
    gotoRequestCards() {
      this.$router.push({
        name: 'batch-cards-request',
      }).catch(error => {
        console.log(error)
      })
    },
    async downloadBatchCards(data) {
      const url = data.cardFileLink
      try {
        const response = await this.downloadBatchCard({ url, responseType: 'blob' })
        const blob = new Blob([response.data], { type: 'application/zip' })
        const downloadUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = downloadUrl
        link.setAttribute('download', 'Batch Cards.zip')
        document.body.appendChild(link)
        link.click()
        this.alertSuccess({ text: this.$t('Your request to download Batch Cards was successful') })

        window.URL.revokeObjectURL(downloadUrl)
        document.body.removeChild(link)
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async fetchBatchCardsRequest() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getBatchRequest({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: eChannelsPermission.VIEW_CARD_REQUESTS,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.batchCardRequestList = data.data
          this.totalCount = data.recordCount
        } else {
          this.batchCardRequestList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async onTakeDelivery(items) {
      const {
        id,
        batchNo,
        requestType,
        numberOfCardsRequested,
        numberOfCardsReceived,
        numberOfCardsAwaitingDelivery,
      } = items
      this.takeDeliveryCardsFormData = {
        id,
        batchNo,
        cardRequestType: requestType,
        totalNoOfCardsRequested: numberOfCardsRequested,
        totalNoOfCardsDelivered: handleZero(numberOfCardsReceived),
        cardsReceived: '',
        numberOfCardsAwaiting: numberOfCardsAwaitingDelivery,
      }
      this.numberOfCards = numberOfCardsAwaitingDelivery
      this.$root.$emit('bv::show::modal', 'take-delivery-modal')
    },
    computeNumberOfCardsAwaitingDelivery(val) {
      if (val < this.numberOfCards) {
        this.takeDeliveryCardsFormData.numberOfCardsAwaiting = this.numberOfCards
      }
    },
    async handleTakeDelivery() {
      try {
        this.hideModal('take-delivery-modal')
        this.isLoading = true
        this.showSpinner = true
        const { status } = await this.takeCardDelivery({
          id: this.takeDeliveryCardsFormData.id,
          noOfCardsReceived: this.takeDeliveryCardsFormData.cardsReceived,
          dateReceived: toDateWithYearFirst(new Date()),
        })
        if (status === 200) {
          this.alertSuccess({ text: this.$('Your request to take card delivery was successful') })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
        this.showSpinner = false
      }
    },
    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.takeDeliveryRules.validate().then(async success => {
        if (success) {
          this.confirm(this.handleTakeDelivery, { text: this.$t('Are you sure you want to take these cards delivery?') })
        }
      })
    },
    async onRevokeCard(items) {
      const { id } = items

      const revokeCardAction = async () => {
        try {
          this.showSpinner = true
          const { status } = await this.revokeCard({ id, reason: '' })
          if (status === 200) {
            this.fetchBatchCardsRequest()
            this.alertSuccess({ text: this.$t('Your request to revoke card was successful') })
          }
        } catch (error) {
          const reasonText = this.$t('Your request to revoke card failed. Reason')
          this.alertError({ text: `${reasonText}: ${this.readError(error)}` })
        } finally {
          this.showSpinner = false
        }
      }
      this.confirm(revokeCardAction, { title: '', text: this.$t('Are you sure you want to revoke these cards?') })
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadCardGenerationReport({
          search: this.filter,
          fileExportFormat: 1,
          permission: eChannelsPermission.VIEW_CARD_REQUESTS,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        } else if (response.status === 504) {
          this.alertSuccessCustom({
            text: this.$t('Your download request is still processing, keep checking the'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('for status.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadCardGenerationReport({
          search: this.filter,
          fileExportFormat: 0,
          permission: eChannelsPermission.VIEW_CARD_REQUESTS,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        } else if (response.status === 504) {
          this.alertSuccessCustom({
            text: this.$t('Your download request is still processing, keep checking the'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('for status.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    resetModal() {
      this.cardProfilesFormData = { ...null }
      this.isLoading = false
      this.isEditable = false
      this.isReadonly = false
      this.buttonLabel = this.$t('Save')
      this.modalTitle = ''
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchBatchCardsRequest()
    },
  },
}
</script>
